import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Components
import CalendarHeader from '../components/calendar/CalendarHeader';
import CalendarGrid from '../components/calendar/CalendarGrid';
import CalendarModeToolbar, { AgendaMode } from '../components/calendar/CalendarModeToolbar';
import CalendarDayStats from '../components/calendar/CalendarDayStats';
import CloseDayModal, { CloseDayStaff } from '../components/calendar/CloseDayModal';
import NewAppointmentModal from '../components/modals/NewAppointmentModal';
import BookingModal from '../components/modals/BookingModal';
import CustomerDetailModal from '../components/modals/CustomerDetailModal';
import QuickSaleModal from '../components/modals/QuickSaleModal';
import SendNotificationModal, { SendNotificationPreset } from '../components/modals/SendNotificationModal';
import ConfirmDialog from '../components/ui/ConfirmDialog';

// Contexts
import { useEvents } from '@/contexts/EventsContext';
import { useUsers } from '@/contexts/UsersContext';
import { useAuth } from '@/contexts/AuthContext';
import { useClients } from '@/contexts/ClientsContext';

// Types
import { StaffMember } from '../components/calendar/types';
import { validateBookingSlot } from '@/lib/bookingValidation';
import { mapRawClientToCustomer } from '@/utils/customerMapper';

const STAFF_COLORS = [
    'bg-orange-100 text-orange-700',
    'bg-blue-100 text-blue-700',
    'bg-purple-100 text-purple-700',
    'bg-emerald-100 text-emerald-700',
    'bg-pink-100 text-pink-700',
    'bg-indigo-100 text-indigo-700',
];

function isSameDay(a: Date, b: Date) {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}

const ALL_STAFF: StaffMember = {
    id: 'all',
    name: 'Tutto lo Staff',
    role: '',
    color: 'bg-gray-800 text-white',
    avatar: 'ALL'
};

const Calendar: React.FC = () => {
    const { t, i18n } = useTranslation();

    // --- DATI REALI ---
    const { bookings, getPerformanceById, getBookingPrice, updateBooking, busySlots, performances, shiftChanges, createBusySlot, deleteBusySlot } = useEvents();
    const { users, getUserById, updateStaffOrder } = useUsers();
    const { user, organization, canSeeAllAgenda, isAdmin } = useAuth();
    const { clients, getClientById, customerReports } = useClients();

    // --- STATE ---
    const [view, setView] = useState<'day' | 'week'>('day');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);

    // Sotto i 768px l'agenda mostra una colonna per volta (stessa soglia di CalendarGrid).
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Su mobile la vista settimanale non esiste: si ricade sempre sulla giornaliera
    useEffect(() => {
        if (isMobile && view === 'week') setView('day');
    }, [isMobile, view]);

    // Modals State
    const [isNewAppointmentOpen, setIsNewAppointmentOpen] = useState(false);
    const [newAppointmentProps, setNewAppointmentProps] = useState<{
        initialDate?: Date;
        initialTime?: string;
        initialStaffId?: string;
        initialPerformanceId?: string;
    }>({});

    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState<any | null>(null);

    // Modalità blocco slot: i click su slot vuoti bloccano/sbloccano invece di creare appuntamenti
    const [isBlockMode, setIsBlockMode] = useState(false);

    // Modalità spostamento: appuntamento selezionato in attesa di un tap sullo slot di destinazione
    const [movingBooking, setMovingBooking] = useState<any | null>(null);
    // Primo step dello spostamento da toolbar: in attesa del tap sull'appuntamento da spostare
    const [isMovePicking, setIsMovePicking] = useState(false);
    // Overlay cancellati (agenda in sola lettura)
    const [showCancelled, setShowCancelled] = useState(false);
    // Chiusura giornata: modale con selezione del dipendente (non è una modalità agenda)
    const [isCloseDayOpen, setIsCloseDayOpen] = useState(false);

    // Una sola modalità agenda alla volta
    const exitAgendaModes = () => {
        setIsBlockMode(false);
        setMovingBooking(null);
        setIsMovePicking(false);
        setShowCancelled(false);
    };
    const agendaMode: AgendaMode = movingBooking ? 'move-target'
        : isMovePicking ? 'move-pick'
            : isBlockMode ? 'block'
                : showCancelled ? 'cancelled'
                    : 'none';

    // CUSTOMER DETAIL MODAL STATE
    const [isCustomerDetailOpen, setIsCustomerDetailOpen] = useState(false);
    const [isQuickSaleOpen, setIsQuickSaleOpen] = useState(false);
    const [isNotifyClientsOpen, setIsNotifyClientsOpen] = useState(false);
    const [selectedCustomerForDetail, setSelectedCustomerForDetail] = useState<any | null>(null);

    // SEARCH: scroll target hour for CalendarGrid
    const [scrollToHour, setScrollToHour] = useState<{ value: number, _t: number } | null>(null);

    // Precompilazione dell'invio notifiche: in vista Giorno il filtro parte già
    // sulla data che si sta guardando; in vista Settimana non c'è un giorno solo
    // da precompilare, quindi il modal si apre su "Tutti".
    const notifyPreset = useMemo<SendNotificationPreset | undefined>(
        () => (view === 'day' ? { mode: 'date', date: currentDate } : undefined),
        [view, currentDate],
    );

    // CONFIRM DROP MODAL
    const [confirmDrop, setConfirmDrop] = useState<{
        isOpen: boolean;
        onConfirm: () => void;
        onCancel: () => void;
    }>({ isOpen: false, onConfirm: () => { }, onCancel: () => { } });

    // 1. Staff
    const staffMembers: StaffMember[] = useMemo(() => {
        let active = users.filter((u: any) => u.is_active !== false);

        // Junior: vede solo la propria agenda (nessuna colonna degli altri).
        if (!canSeeAllAgenda && user) {
            active = active.filter((u: any) => u.id === user.id);
        }

        // Se l'admin ha fissato un ordine manuale (trascinando le colonne in agenda),
        // quello vince su tutto. Altrimenti resta il comportamento storico:
        // l'utente loggato per primo, poi in ordine alfabetico.
        const hasManualOrder = active.some((u: any) => u.sort_order !== null && u.sort_order !== undefined);

        active.sort((a: any, b: any) => {
            if (hasManualOrder) {
                const orderA = a.sort_order ?? Number.MAX_SAFE_INTEGER;
                const orderB = b.sort_order ?? Number.MAX_SAFE_INTEGER;
                if (orderA !== orderB) return orderA - orderB;
            } else if (user) {
                if (a.id === user.id) return -1;
                if (b.id === user.id) return 1;
            }
            const nameA = `${a.first_name} ${a.last_name}`;
            const nameB = `${b.first_name} ${b.last_name}`;
            return nameA.localeCompare(nameB);
        });

        return active.map((u: any, index: number) => {
            const initials = `${u.first_name?.[0] ?? ''}${u.last_name?.[0] ?? ''}`.toUpperCase();
            return {
                id: u.id,
                name: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || 'Staff',
                role: u.role === 'admin' ? 'Owner' : 'Staff',
                color: STAFF_COLORS[index % STAFF_COLORS.length],
                avatar: initials || 'S',
                image: undefined,
            };
        });
    }, [users, user, canSeeAllAgenda]);

    // Set Default Staff: su schermo largo la vista d'insieme, su mobile una colonna sola.
    useEffect(() => {
        if (staffMembers.length === 0) return;

        const ownColumn = user ? staffMembers.find((s) => s.id === user.id) : undefined;

        if (!selectedStaff) {
            // Su mobile cinque colonne non ci stanno: si parte dalla propria agenda
            // e si passa a quella di un collega dal selettore.
            setSelectedStaff(isMobile ? (ownColumn ?? staffMembers[0]) : (user ? ALL_STAFF : staffMembers[0]));
            return;
        }

        // Restringendo la finestra (o tornando da desktop) la vista d'insieme non regge:
        // si ricade sulla propria colonna.
        if (isMobile && selectedStaff.id === 'all') {
            setSelectedStaff(ownColumn ?? staffMembers[0]);
        }
    }, [selectedStaff, staffMembers, user, isMobile]);

    // 2. Week Calculation (Omitting closed days)
    const weekDays = useMemo(() => {
        // Mostra SEMPRE tutti i 7 giorni (lun→dom): le giornate chiuse restano visibili
        // in agenda come colonne bloccate (grigie con etichetta "Chiuso"), non nascoste.
        const days: Date[] = [];
        const current = new Date(currentDate);
        const day = current.getDay();
        const diff = current.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(current.setDate(diff));

        for (let i = 0; i < 7; i++) {
            const d = new Date(monday);
            d.setDate(monday.getDate() + i);
            days.push(d);
        }
        return days;
    }, [currentDate]);

    // --- DYNAMIC HOURS LOGIC ---
    const { startHour, endHour, hasOpenDays } = useMemo(() => {
        const parseTime = (t?: string | null) => {
            if (!t) return null;
            const [h, m] = t.split(':').map(Number);
            return h + m / 60;
        };

        const timeRanges = organization?.time_ranges || {};
        const daysOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

        let minStart = 24;
        let maxEnd = 0;
        let hasOpenDays = false;

        const isAllStaffView = !selectedStaff || selectedStaff.id === 'all';

        if (view === 'day') {
            // Visualizzazione giornaliera: orario salone del giorno corrente
            const dayName = daysOrder[currentDate.getDay()];
            const dayTimes = timeRanges[dayName];

            if (dayTimes?.open && dayTimes?.close) {
                const s = parseTime(dayTimes.open);
                const e = parseTime(dayTimes.close);
                if (s !== null && e !== null) {
                    minStart = s;
                    maxEnd = e;
                    hasOpenDays = true;
                }
            }

            // Cambi turno del giorno: espandono la visualizzazione
            const dStr = format(currentDate, 'yyyy-MM-dd');
            const dayShiftChanges = (shiftChanges || []).filter((s: any) =>
                s.shift_date === dStr && (isAllStaffView || s.hairdresser_id === selectedStaff!.id)
            );
            dayShiftChanges.forEach((sc: any) => {
                const s = parseTime(sc.start_time);
                const e = parseTime(sc.end_time);
                if (s !== null && e !== null) {
                    hasOpenDays = true;
                    if (s < minStart) minStart = s;
                    if (e > maxEnd) maxEnd = e;
                }
            });

            // Finestre di disponibilità extra: idem, uno slot sbloccato deve essere visibile
            (busySlots || []).filter((bs: any) =>
                bs.type === 'extra' && (bs.start_date || '').slice(0, 10) === dStr &&
                (isAllStaffView || bs.hairdresser_id === selectedStaff!.id)
            ).forEach((bs: any) => {
                const s = parseTime(bs.start_time);
                const e = parseTime(bs.end_time);
                if (s !== null && e !== null) {
                    hasOpenDays = true;
                    if (s < minStart) minStart = s;
                    if (e > maxEnd) maxEnd = e;
                }
            });
        } else {
            // Visualizzazione settimanale: minore apertura settimanale e maggiore chiusura
            daysOrder.forEach(day => {
                const dayTimes = timeRanges[day];
                if (dayTimes?.open && dayTimes?.close) {
                    const s = parseTime(dayTimes.open);
                    const e = parseTime(dayTimes.close);
                    if (s !== null && e !== null) {
                        hasOpenDays = true;
                        if (s < minStart) minStart = s;
                        if (e > maxEnd) maxEnd = e;
                    }
                }
            });

            // Cambi turno della settimana: espandono la visualizzazione
            const weekDatesStrs = weekDays.map(d => format(d, 'yyyy-MM-dd'));
            const weekShiftChanges = (shiftChanges || []).filter((s: any) =>
                weekDatesStrs.includes(s.shift_date) && (isAllStaffView || s.hairdresser_id === selectedStaff!.id)
            );
            weekShiftChanges.forEach((sc: any) => {
                const s = parseTime(sc.start_time);
                const e = parseTime(sc.end_time);
                if (s !== null && e !== null) {
                    hasOpenDays = true;
                    if (s < minStart) minStart = s;
                    if (e > maxEnd) maxEnd = e;
                }
            });

            // Finestre di disponibilità extra della settimana
            (busySlots || []).filter((bs: any) =>
                bs.type === 'extra' && weekDatesStrs.includes((bs.start_date || '').slice(0, 10)) &&
                (isAllStaffView || bs.hairdresser_id === selectedStaff!.id)
            ).forEach((bs: any) => {
                const s = parseTime(bs.start_time);
                const e = parseTime(bs.end_time);
                if (s !== null && e !== null) {
                    hasOpenDays = true;
                    if (s < minStart) minStart = s;
                    if (e > maxEnd) maxEnd = e;
                }
            });
        }

        if (!hasOpenDays) {
            return { startHour: 8, endHour: 21, hasOpenDays: false }; // Default fallback
        }

        return {
            startHour: minStart,
            endHour: maxEnd,
            hasOpenDays: true
        };
    }, [organization, view, currentDate, weekDays, shiftChanges, busySlots, selectedStaff]);

    const hours = useMemo(() => {
        // Genera array di ore basato su startHour/endHour dinamici frazionari
        const numBlocks = Math.ceil(endHour - startHour);
        return Array.from({ length: numBlocks }, (_, i) => i + startHour);
    }, [startHour, endHour]);

    // 3. Events Mapping
    // Prima prenotazione del cliente in questo salone: sia che arrivi dall'app, sia che
    // il salone lo abbia inserito a mano, l'appuntamento più vecchio (non cancellato)
    // di ogni cliente è la sua "prima volta" e in agenda si riconosce da una stellina.
    const firstBookingIds = useMemo(() => {
        const firstByClient = new Map<string, { id: any; when: string }>();
        (bookings || []).forEach((b: any) => {
            if (!b.client_id || !b.date || b.status === 'cancelled') return;
            const when = `${b.date}T${b.time ?? '00:00'}`;
            const current = firstByClient.get(b.client_id);
            if (!current || when < current.when) firstByClient.set(b.client_id, { id: b.id, when });
        });
        return new Set(Array.from(firstByClient.values()).map(v => v.id));
    }, [bookings]);

    const displayEvents = useMemo(() => {
        if (!selectedStaff) return [];

        const weekMap = weekDays.reduce<Record<string, number>>((acc, d, idx) => {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const key = `${yyyy}-${mm}-${dd}`;
            acc[key] = idx;
            return acc;
        }, {});

        const isAllStaff = selectedStaff.id === 'all';

        // Colori del mockup: arancione chiaro per i servizi, rosso pieno per i no-show
        // (#FCA5A5 con bordo #DC2626), così saltano all'occhio scorrendo la giornata.
        const SERVICE_COLOR = 'bg-orange-100 border-orange-500 text-gray-900';
        const NO_SHOW_COLOR = 'bg-red-300 border-red-600 text-gray-900';

        // Multicolore opzionale: se abilitato dal salone, ogni appuntamento (non no-show)
        // riceve un colore diverso a rotazione, come nelle vecchie versioni dell'agenda.
        const multicolorEnabled = organization?.multicolor_appointments_enabled === true;
        const EVENT_COLORS = [
            'bg-orange-100 border-orange-500 text-orange-900',
            'bg-purple-100 border-purple-500 text-purple-900',
            'bg-red-100 border-red-500 text-red-900',
            'bg-blue-100 border-blue-500 text-blue-900',
            'bg-emerald-100 border-emerald-500 text-emerald-900',
        ];

        const reportedClientIds = new Set(customerReports.map((r: any) => r.client_id));

        return bookings
            .filter((b: any) => {
                // Se All Staff, non filtro per id. Se specifico, filtro.
                // In All Staff, mostro solo gli appuntamenti di staff ancora presenti

                const staffExists = staffMembers.some(s => s.id === b.hairdresser_id);
                const staffMatch = isAllStaff ? staffExists : b.hairdresser_id === selectedStaff.id;
                const statusMatch = b.status === 'confirmed' || b.status === 'no_show';
                return staffMatch && statusMatch && b.time;
            })
            .map((b: any, index: number) => {
                const dateString: string = b.date;
                const dayIndex = weekMap[dateString];
                // In Week View, dayIndex serves to valid date. In Day view, we verify date.
                if (typeof dayIndex !== 'number') {
                    // Se l'evento non è nella settimana corrente, potrebbe non interessarci per la Week view.
                    // Ma per la Day view, dobbiamo controllare se è il giorno corrente.
                    const eventDate = new Date(b.date);
                    if (!isSameDay(eventDate, currentDate)) return null;
                }

                const [h, m] = (b.time as string).split(':').map(Number);
                const startHour = h + m / 60;

                const performance = getPerformanceById(b.performance_id);
                const durationMinutes = performance?.duration || 30;
                const duration = durationMinutes / 60;

                const client = getClientById(b.client_id);
                const color = b.status === 'no_show'
                    ? NO_SHOW_COLOR
                    : (multicolorEnabled ? EVENT_COLORS[index % EVENT_COLORS.length] : SERVICE_COLOR);

                const eventData = {
                    id: b.id,
                    staffId: b.hairdresser_id,
                    dayIndex,
                    startHour,
                    duration,
                    title: performance?.name ?? 'Servizio',
                    client: client?.full_name ?? 'Cliente Sconosciuto',
                    status: b.status,
                    color: color,
                    isReported: reportedClientIds.has(b.client_id),
                    // L'importo, non solo il fatto che ci sia: il badge nello slot lo mostra.
                    priceAdjustment: Number(b.price_adjustment) || 0,
                    isFirstBooking: firstBookingIds.has(b.id),
                    booking: b
                };
                return eventData;
            })
            .filter((e: any) => e !== null)
            .filter((e: any) => {
                if (view === 'week') {
                    // In week view, filtering for day index
                    return e.dayIndex !== undefined;
                } else {
                    // In day view, we already checked date above (sort of).
                    // But strictly:
                    const eventDate = new Date(e.booking.date);
                    return isSameDay(eventDate, currentDate);
                }
            });
    }, [bookings, selectedStaff, weekDays, view, currentDate, getPerformanceById, getClientById, firstBookingIds]);

    // Slot liberi (verdi) per la modalità spostamento: per ogni colonna della vista,
    // i range in cui l'appuntamento selezionato può atterrare (validazione completa).
    const moveTargets = useMemo(() => {
        if (!movingBooking) return null;
        const stepMin = organization?.step || 15;
        const stepH = stepMin / 60;

        const isParallel = view === 'day' && selectedStaff?.id === 'all';
        const columns: { date: Date; staffId?: string }[] = isParallel
            ? staffMembers.map((s) => ({ date: currentDate, staffId: s.id }))
            : view === 'week'
                ? weekDays.map((d) => ({ date: d, staffId: selectedStaff && selectedStaff.id !== 'all' ? selectedStaff.id : undefined }))
                : [{ date: currentDate, staffId: selectedStaff && selectedStaff.id !== 'all' ? selectedStaff.id : undefined }];

        const ctx = { bookings, performances, busySlots, organization, users, shiftChanges };

        return columns.map((col) => {
            if (!col.staffId) return [] as { start: number; end: number }[];
            const ranges: { start: number; end: number }[] = [];
            for (let h = startHour; h + stepH <= endHour + 1e-6; h += stepH) {
                const hh = Math.floor(h);
                const mm = Math.round((h - hh) * 60);
                const timeStr = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
                const ok = validateBookingSlot(ctx, {
                    date: col.date,
                    time: timeStr,
                    performanceId: movingBooking.performance_id,
                    staffId: col.staffId,
                    excludeBookingId: movingBooking.id,
                }).isValid;
                if (ok) {
                    const last = ranges[ranges.length - 1];
                    if (last && Math.abs(last.end - h) < 1e-6) last.end = h + stepH;
                    else ranges.push({ start: h, end: h + stepH });
                }
            }
            return ranges;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [movingBooking, view, selectedStaff, staffMembers, currentDate, weekDays, startHour, endHour, bookings, performances, busySlots, organization, users, shiftChanges]);

    // Prenotazioni cancellate della vista corrente (overlay modalità "Cancellati")
    const cancelledEvents = useMemo(() => {
        if (!showCancelled) return null;
        const isParallel = view === 'day' && selectedStaff?.id === 'all';
        const weekStrs = weekDays.map((d) => format(d, 'yyyy-MM-dd'));
        const dayStr = format(currentDate, 'yyyy-MM-dd');

        return bookings
            .filter((b: any) => {
                if (b.status !== 'cancelled' || !b.time) return false;
                return view === 'week' ? weekStrs.includes(b.date) : b.date === dayStr;
            })
            .map((b: any) => {
                const [h, m] = b.time.slice(0, 5).split(':').map(Number);
                const colIndex = isParallel
                    ? staffMembers.findIndex((s) => s.id === b.hairdresser_id)
                    : view === 'week'
                        ? weekStrs.indexOf(b.date)
                        : (selectedStaff && selectedStaff.id !== 'all' && b.hairdresser_id !== selectedStaff.id ? -1 : 0);
                return {
                    id: b.id,
                    colIndex,
                    startHour: h + m / 60,
                    duration: (getPerformanceById(b.performance_id)?.duration || 30) / 60,
                    clientName: getClientById(b.client_id)?.full_name || t('bookings.modal.client_not_found', 'Cliente'),
                    cancelledAt: b.cancelled_at || null,
                };
            })
            .filter((e: any) => e.colIndex >= 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCancelled, bookings, view, selectedStaff, staffMembers, currentDate, weekDays, getPerformanceById, getClientById]);

    // Single validation+update entry point for all drag operations
    const applyBookingChange = async (
        booking: any,
        changes: { newStartHour?: number; newStaffId?: string; newDateStr?: string }
    ) => {
        const originalPerf = performances.find((p: any) => p.id === booking.performance_id);
        if (!originalPerf) {
            toast.error(t('calendar.no_service_for_staff', 'Servizio non trovato.'));
            throw new Error('No performance found');
        }

        let targetPerfId = booking.performance_id;
        let finalStaffId = booking.hairdresser_id;
        let finalDate = booking.date;
        let finalTime = booking.time.slice(0, 5);
        const payload: any = {};

        if (changes.newStaffId) {
            const targetPerf = performances.find((p: any) =>
                p.hairdresser_id === changes.newStaffId &&
                p.name === originalPerf.name &&
                p.price === originalPerf.price &&
                p.duration === originalPerf.duration &&
                p.is_active !== false
            );
            if (!targetPerf) {
                toast.error(t('calendar.staff_no_service', 'Il dipendente non offre questo servizio con la stessa durata/prezzo.'));
                throw new Error('Staff cannot provide this service');
            }
            finalStaffId = changes.newStaffId;
            targetPerfId = targetPerf.id;
            payload.hairdresser_id = finalStaffId;
            payload.performance_id = targetPerfId;
        }

        if (changes.newDateStr) {
            finalDate = changes.newDateStr;
            payload.date = finalDate;
        }

        if (changes.newStartHour !== undefined) {
            const h = Math.floor(changes.newStartHour);
            const m = Math.round((changes.newStartHour % 1) * 60);
            finalTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
            payload.time = `${finalTime}:00`;
        }

        const validationResult = validateBookingSlot(
            { bookings, performances, busySlots, organization, users, shiftChanges },
            { date: new Date(finalDate), time: finalTime, performanceId: targetPerfId, staffId: finalStaffId, excludeBookingId: booking.id }
        );
        if (!validationResult.isValid) {
            console.log(validationResult);
            toast.error(t(validationResult.errorKey || 'calendar.overlapping_error', 'Impossibile spostare l\'appuntamento.'));
            throw new Error(validationResult.errorKey);
        }

        return new Promise<void>((resolve, reject) => {
            setConfirmDrop({
                isOpen: true,
                onConfirm: async () => {
                    setConfirmDrop(prev => ({ ...prev, isOpen: false }));
                    try {
                        const result = await updateBooking(booking.id, payload);
                        if (!result?.success) {
                            toast.error(result?.error || t('common.error', 'Errore imprevisto'));
                            reject(new Error('Update failed'));
                            return;
                        }
                        toast.success(t('calendar.event_updated', 'Appuntamento aggiornato'));
                        resolve();
                    } catch (error) {
                        reject(error);
                    }
                },
                onCancel: () => {
                    setConfirmDrop(prev => ({ ...prev, isOpen: false }));
                    reject(new Error('Cancelled by user'));
                }
            });
        });
    };

    const handleEventVerticalDrop = (booking: any, newStartHour: number) =>
        applyBookingChange(booking, { newStartHour });

    const handleEventHorizontalDrop = (booking: any, newStaffId?: string, newDateStr?: string) =>
        applyBookingChange(booking, { newStaffId, newDateStr });

    const handleEventCombinedDrop = (booking: any, newStaffId?: string, newDateStr?: string, newStartHour?: number) =>
        applyBookingChange(booking, { newStaffId, newDateStr, newStartHour });

    const navigateDate = (direction: 'prev' | 'next') => {
        const newDate = new Date(currentDate);
        if (view === 'week') {
            newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));
            setCurrentDate(newDate);
            return;
        }

        // Daily view: naviga in avanti o indietro saltando i giorni di chiusura
        let step = direction === 'next' ? 1 : -1;
        const daysOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const timeRanges = organization?.time_ranges || {};

        const isAllStaffNav = !selectedStaff || selectedStaff.id === 'all';

        let foundOpenDay = false;
        // Limite al loop (max 7) per evitare cicli infiniti se non ci sono orari configurati
        for (let i = 0; i < 7; i++) {
            newDate.setDate(newDate.getDate() + step);
            const dayName = daysOrder[newDate.getDay()];
            const dayTimes = timeRanges[dayName];
            const dStr = format(newDate, 'yyyy-MM-dd');
            const hasShiftChange = (shiftChanges || []).some((s: any) =>
                s.shift_date === dStr && (isAllStaffNav || s.hairdresser_id === selectedStaff!.id)
            );
            const hasExtra = (busySlots || []).some((bs: any) =>
                bs.type === 'extra' && (bs.start_date || '').slice(0, 10) === dStr &&
                (isAllStaffNav || bs.hairdresser_id === selectedStaff!.id)
            );
            if ((dayTimes?.open && dayTimes?.close) || hasShiftChange || hasExtra) {
                foundOpenDay = true;
                break;
            }
            step = direction === 'next' ? 1 : -1;
        }

        if (!foundOpenDay) {
            // Se tutto è chiuso, esegui uno step ingenuo
            newDate.setTime(currentDate.getTime());
            newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
        }

        setCurrentDate(newDate);
    };

    // Auto-avanzo se la vista è "day", il giorno corrente è chiuso e l'utente apre il calendario oggi
    useEffect(() => {
        if (view === 'day' && organization?.time_ranges && !hasOpenDays) {
            const daysOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const timeRanges = organization.time_ranges;
            const isAllStaffAuto = !selectedStaff || selectedStaff.id === 'all';
            let newDate = new Date(currentDate);
            let found = false;
            for (let i = 1; i <= 7; i++) {
                newDate.setDate(newDate.getDate() + 1);
                const dayName = daysOrder[newDate.getDay()];
                const dayTimes = timeRanges[dayName];
                const dStr = format(newDate, 'yyyy-MM-dd');
                const hasShiftChange = (shiftChanges || []).some((s: any) =>
                    s.shift_date === dStr && (isAllStaffAuto || s.hairdresser_id === selectedStaff!.id)
                );
                const hasExtra = (busySlots || []).some((bs: any) =>
                    bs.type === 'extra' && (bs.start_date || '').slice(0, 10) === dStr &&
                    (isAllStaffAuto || bs.hairdresser_id === selectedStaff!.id)
                );
                if ((dayTimes?.open && dayTimes?.close) || hasShiftChange || hasExtra) {
                    found = true;
                    break;
                }
            }
            if (found) {
                setCurrentDate(newDate);
            }
        }
    }, [view, hasOpenDays, organization, selectedStaff]);

    const locale = i18n.language === 'en' ? 'en-US' : 'it-IT';
    const titleFormatter = new Intl.DateTimeFormat(locale, {
        weekday: view === 'day' ? 'long' : undefined,
        day: view === 'day' ? 'numeric' : undefined,
        month: 'long',
        year: view === 'week' ? 'numeric' : undefined
    });

    // Riordino manuale delle colonne dell'agenda (solo admin): l'ordine vale per tutti
    // e viene salvato su Hairdressers.sort_order.
    const handleStaffReorder = async (orderedIds: string[]) => {
        const res = await updateStaffOrder(orderedIds);
        if (!res?.success) {
            toast.error(t('calendar.staff_order.error', "Non è stato possibile salvare l'ordine dei collaboratori."));
        } else {
            toast.success(t('calendar.staff_order.saved', 'Ordine dei collaboratori aggiornato.'));
        }
    };

    // Blocca/sblocca un singolo slot (solo per quel giorno) per un dipendente.
    // Slot bianco → grigio: BusySlot type 'block'. Slot grigio → bianco: si rimuove il
    // blocco oppure, se il grigio viene dall'orario (salone/dipendente/pausa), si crea
    // una finestra di disponibilità extra (BusySlot type 'extra') per quel solo slot.
    // Un tap su uno slot bianco-da-extra rimuove la finestra e lo slot torna grigio.
    const handleBlockSlotToggle = async (date: Date, time: string, staffId?: string) => {
        const targetStaffId = staffId && staffId !== 'all'
            ? staffId
            : (selectedStaff && selectedStaff.id !== 'all' ? selectedStaff.id : undefined);
        if (!targetStaffId) {
            toast.error(t('calendar.block_select_staff', 'Seleziona un dipendente per bloccare uno slot.'));
            return;
        }

        const slotStep = organization?.step || 15;
        const dStr = format(date, 'yyyy-MM-dd');
        const timeToMin = (v: string) => {
            const [hh, mm] = v.slice(0, 5).split(':').map(Number);
            return hh * 60 + (mm || 0);
        };
        const toTimeStr = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
        const startMin = timeToMin(time);
        const endMin = startMin + slotStep;

        // Slot bloccato a mano → sblocca
        const existing = (busySlots || []).find((bs: any) => {
            if (bs.type !== 'block' || bs.hairdresser_id !== targetStaffId) return false;
            if ((bs.start_date || '').slice(0, 10) !== dStr) return false;
            if (!bs.start_time || !bs.end_time) return false;
            return startMin < timeToMin(bs.end_time) && endMin > timeToMin(bs.start_time);
        });
        if (existing) {
            const res = await deleteBusySlot(existing.id);
            if (res.success) toast.success(t('calendar.slot_unblocked', 'Slot sbloccato'));
            else toast.error(res.error || t('common.error'));
            return;
        }

        // Ferie e permessi non si toccano da qui: si gestiscono dalla scheda Staff
        const inHolidayOrPermit = (busySlots || []).some((bs: any) => {
            if (bs.type !== 'permit' && bs.type !== 'holiday') return false;
            if (bs.hairdresser_id && bs.hairdresser_id !== targetStaffId) return false;
            const s = (bs.start_date || '').slice(0, 10);
            const e = ((bs.end_date || bs.start_date) || '').slice(0, 10);
            if (s > dStr || e < dStr) return false;
            if (bs.type === 'holiday' || bs.is_all_day || (!bs.start_time && !bs.end_time)) return true;
            return startMin < timeToMin(bs.end_time) && endMin > timeToMin(bs.start_time);
        });
        if (inHolidayOrPermit) {
            toast.error(t('calendar.block_holiday_conflict', 'Slot coperto da ferie o permesso: gestiscilo dalla scheda Staff.'));
            return;
        }

        // Appuntamento nello slot: né blocchi né rimozioni di finestre extra
        const hasBooking = bookings.some((b: any) => {
            if (b.hairdresser_id !== targetStaffId || b.date !== dStr) return false;
            if (b.status === 'cancelled' || b.status === 'no_show') return false;
            const perf = getPerformanceById(b.performance_id);
            const bStart = timeToMin(b.time || '');
            return startMin < bStart + (perf?.duration || 30) && endMin > bStart;
        });
        if (hasBooking) {
            toast.error(t('calendar.block_has_booking', "C'è già un appuntamento in questo slot."));
            return;
        }

        // Slot bianco grazie a una finestra extra → il tap la rimuove (torna grigio)
        const existingExtra = (busySlots || []).find((bs: any) => {
            if (bs.type !== 'extra' || bs.hairdresser_id !== targetStaffId) return false;
            if ((bs.start_date || '').slice(0, 10) !== dStr) return false;
            if (!bs.start_time || !bs.end_time) return false;
            return startMin < timeToMin(bs.end_time) && endMin > timeToMin(bs.start_time);
        });
        if (existingExtra) {
            const res = await deleteBusySlot(existingExtra.id);
            if (res.success) toast.success(t('calendar.slot_relocked', 'Slot di nuovo non disponibile'));
            else toast.error(res.error || t('common.error'));
            return;
        }

        // Slot grigio da orario (dipendente non in turno, pausa, salone chiuso)?
        // Il cambio turno, se presente, sostituisce l'orario normale intersecato col salone.
        const daysOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = daysOrder[date.getDay()];
        const sc = (shiftChanges || []).find((x: any) => x.shift_date === dStr && x.hairdresser_id === targetStaffId);

        let avail: { open: number; close: number; breakStart: number | null; breakEnd: number | null } | null = null;
        if (sc) {
            avail = {
                open: timeToMin(sc.start_time),
                close: timeToMin(sc.end_time),
                breakStart: sc.start_break ? timeToMin(sc.start_break) : null,
                breakEnd: sc.end_break ? timeToMin(sc.end_break) : null,
            };
        } else {
            const shopSched = organization?.time_ranges?.[dayName];
            const staffSched = (getUserById(targetStaffId) as any)?.time_ranges?.[dayName];
            if (shopSched?.open && shopSched?.close && staffSched?.open && staffSched?.close) {
                const open = Math.max(timeToMin(shopSched.open), timeToMin(staffSched.open));
                const close = Math.min(timeToMin(shopSched.close), timeToMin(staffSched.close));
                const bS = staffSched.start_break || shopSched.start_break;
                const bE = staffSched.end_break || shopSched.end_break;
                if (close > open) {
                    avail = { open, close, breakStart: bS ? timeToMin(bS) : null, breakEnd: bE ? timeToMin(bE) : null };
                }
            }
        }

        const inBreak = avail !== null && avail.breakStart !== null && avail.breakEnd !== null
            && startMin < avail.breakEnd && endMin > avail.breakStart;
        const isSlotAvailable = avail !== null && startMin >= avail.open && endMin <= avail.close && !inBreak;

        // Slot grigio da orario → finestra di disponibilità extra per quel solo slot
        if (!isSlotAvailable) {
            const res = await createBusySlot({
                shop_id: organization.id,
                hairdresser_id: targetStaffId,
                type: 'extra',
                start_date: dStr,
                end_date: dStr,
                start_time: toTimeStr(startMin),
                end_time: toTimeStr(endMin),
            });
            if (res.success) toast.success(t('calendar.slot_unblocked', 'Slot sbloccato'));
            else toast.error(res.error || t('common.error'));
            return;
        }

        // Slot bianco normale → blocco manuale
        const res = await createBusySlot({
            shop_id: organization.id,
            hairdresser_id: targetStaffId,
            type: 'block',
            start_date: dStr,
            end_date: dStr,
            start_time: toTimeStr(startMin),
            end_time: toTimeStr(endMin),
        });
        if (res.success) toast.success(t('calendar.slot_blocked', 'Slot bloccato'));
        else toast.error(res.error || t('common.error'));
    };

    // Chiude/riapre la giornata di un dipendente (BusySlot 'holiday' di un giorno)
    const [confirmCloseDay, setConfirmCloseDay] = useState<{ staffId: string; name: string; count: number } | null>(null);
    const [isTogglingDay, setIsTogglingDay] = useState(false);

    // Stato della giornata per ogni dipendente, per la modale "Chiudi giornata"
    const closeDayStaff: CloseDayStaff[] = useMemo(() => {
        const dStr = format(currentDate, 'yyyy-MM-dd');
        return staffMembers.map((s) => {
            const isClosed = (busySlots || []).some((bs: any) =>
                bs.type === 'holiday' && bs.hairdresser_id === s.id &&
                (bs.start_date || '').slice(0, 10) === dStr && ((bs.end_date || bs.start_date) || '').slice(0, 10) === dStr);
            // Ferie multi-giorno: si gestiscono dalla scheda Staff, non da qui
            const isLocked = !isClosed && (busySlots || []).some((bs: any) =>
                bs.type === 'holiday' && bs.hairdresser_id === s.id &&
                bs.start_date <= dStr && (bs.end_date || bs.start_date) >= dStr);
            const bookingsCount = bookings.filter((b: any) =>
                b.hairdresser_id === s.id && b.date === dStr && b.status === 'confirmed').length;
            return { ...s, isClosed, isLocked, bookingsCount };
        });
    }, [staffMembers, busySlots, bookings, currentDate]);

    const doCloseStaffDay = async (staffId: string) => {
        const dStr = format(currentDate, 'yyyy-MM-dd');
        setIsTogglingDay(true);
        try {
            const res = await createBusySlot({
                shop_id: organization.id,
                hairdresser_id: staffId,
                type: 'holiday',
                start_date: dStr,
                end_date: dStr,
            });
            if (res.success) {
                toast.success(t('calendar.editday.closed', 'Giornata chiusa'));
                setIsCloseDayOpen(false);
            } else toast.error(res.error || t('common.error'));
        } finally {
            setIsTogglingDay(false);
            setConfirmCloseDay(null);
        }
    };

    const handleToggleStaffDay = async (staffId: string) => {
        if (!organization?.id || isTogglingDay) return;
        const dStr = format(currentDate, 'yyyy-MM-dd');

        // Giornata già chiusa (ferie di UN giorno)? → riapri. Ferie multi-giorno non si toccano da qui.
        const existing = (busySlots || []).find((bs: any) =>
            bs.type === 'holiday' && bs.hairdresser_id === staffId &&
            (bs.start_date || '').slice(0, 10) === dStr && ((bs.end_date || bs.start_date) || '').slice(0, 10) === dStr);
        if (existing) {
            setIsTogglingDay(true);
            try {
                const res = await deleteBusySlot(existing.id);
                if (res.success) {
                    toast.success(t('calendar.editday.reopened', 'Giornata riaperta'));
                    setIsCloseDayOpen(false);
                } else toast.error(res.error || t('common.error'));
            } finally {
                setIsTogglingDay(false);
            }
            return;
        }
        const inLongHoliday = (busySlots || []).some((bs: any) =>
            bs.type === 'holiday' && bs.hairdresser_id === staffId &&
            bs.start_date <= dStr && (bs.end_date || bs.start_date) >= dStr);
        if (inLongHoliday) {
            toast.error(t('calendar.editday.long_holiday', 'Il dipendente è in ferie: gestiscile dalla scheda Staff.'));
            return;
        }

        const count = bookings.filter((b: any) =>
            b.hairdresser_id === staffId && b.date === dStr && b.status === 'confirmed').length;
        if (count > 0) {
            const staff = staffMembers.find((s) => s.id === staffId);
            setConfirmCloseDay({ staffId, name: staff?.name || '', count });
            return;
        }
        await doCloseStaffDay(staffId);
    };

    const handleEmptySlotClick = (date: Date, time: string, staffId?: string) => {
        if (showCancelled) return; // agenda in sola lettura in questa modalità

        if (isBlockMode) {
            handleBlockSlotToggle(date, time, staffId);
            return;
        }

        // Modalità spostamento: il tap sul buco sposta l'appuntamento selezionato (riusa applyBookingChange)
        if (movingBooking) {
            const [h, m] = time.split(':').map(Number);
            const targetStaffId = staffId && staffId !== 'all'
                ? staffId
                : (selectedStaff && selectedStaff.id !== 'all' ? selectedStaff.id : movingBooking.hairdresser_id);
            applyBookingChange(movingBooking, {
                newStaffId: targetStaffId !== movingBooking.hairdresser_id ? targetStaffId : undefined,
                newDateStr: format(date, 'yyyy-MM-dd'),
                newStartHour: h + m / 60,
            })
                .then(() => setMovingBooking(null))
                .catch(() => { /* slot non valido o annullato: resto in modalità spostamento */ });
            return;
        }

        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = days[date.getDay()];
        const shopSched = organization?.time_ranges?.[dayName];
        const isCurrentlyClosed = !shopSched || !shopSched.open;

        if (isCurrentlyClosed) {
            // Consenti se qualche staff ha un cambio turno o una finestra extra quel giorno
            const dStr = format(date, 'yyyy-MM-dd');
            const hasShiftChange = (shiftChanges || []).some((s: any) => s.shift_date === dStr);
            const hasExtra = (busySlots || []).some((bs: any) =>
                bs.type === 'extra' && (bs.start_date || '').slice(0, 10) === dStr);
            if (!hasShiftChange && !hasExtra) {
                toast.error(t('calendar.shop_closed', "Il salone è chiuso in questo giorno."));
                return;
            }
        }

        // --- ESTRAZIONE LOGICA DI CONTROLLO DISPONIBILITA' TRAMITE UTILITY ---
        // Ritorna l'intero risultato: l'errorKey distingue "fuori orario" da
        // "nessun servizio assegnato" ecc., così il toast dice il motivo vero.
        const validateSlotForStaff = (
            checkStaffId: string,
            checkPerformanceId: string
        ) => {
            // Evaluates standard constraints (working hours, holidays, breaks) AND overlaps
            return validateBookingSlot(
                {
                    bookings,
                    performances,
                    busySlots,
                    organization,
                    users,
                    shiftChanges
                },
                {
                    date,
                    time,
                    performanceId: checkPerformanceId,
                    staffId: checkStaffId
                }
            );
        };
        const isStaffAvailableForSlot = (checkStaffId: string, checkPerformanceId: string): boolean =>
            validateSlotForStaff(checkStaffId, checkPerformanceId).isValid;

        let targetStaffId = staffId && staffId !== 'all' ? staffId : undefined;
        let bestPerformanceId: string | undefined = undefined;

        if (targetStaffId) {
            // Staff esplicito (click su una colonna specifica determinata)
            const staffPerfs = performances.filter((p: any) => p.hairdresser_id == targetStaffId && p.is_active !== false);
            const staffPerf = staffPerfs.reduce((best: any, p: any) =>
                !best || (p.duration ?? Infinity) < (best.duration ?? Infinity) ? p : best, null);
            bestPerformanceId = staffPerf?.id;

            // Verifica disponibilità dello staff specifico: in caso di esito negativo
            // il messaggio riporta la causa reale (es. dipendente senza servizi).
            const availability = validateSlotForStaff(targetStaffId, bestPerformanceId || '');
            if (!availability.isValid) {
                toast.error(t(
                    availability.errorKey || 'calendar.staff_not_working_or_busy',
                    "Il dipendente non è disponibile in questo orario."
                ));
                return;
            }
        } else {
            // Nessuno staff esplicito (es. vista settimanale con "Tutto lo Staff" e appuntamenti paralleli)
            // Cerchiamo il primo dipendente REALMENTE libero a quell'ora
            let foundFreeStaff = false;

            // Prima proviamo con l'utente corrente, se è un parrucchiere ed è libero
            if (user?.id) {
                const staffPerfs = performances.filter((p: any) => p.hairdresser_id == user.id && p.is_active !== false);
                const staffPerf = staffPerfs.reduce((best: any, p: any) =>
                    !best || (p.duration ?? Infinity) < (best.duration ?? Infinity) ? p : best, null);
                bestPerformanceId = staffPerf?.id;
                if (staffPerf && isStaffAvailableForSlot(user.id, staffPerf.id)) {
                    targetStaffId = user.id;
                    bestPerformanceId = staffPerf.id;
                    foundFreeStaff = true;
                }
            }

            // Altrimenti cerchiamo tra tutti gli altri
            if (!foundFreeStaff) {
                for (const staff of staffMembers) {
                    const staffPerfs = performances.filter((p: any) => p.hairdresser_id == staff.id && p.is_active !== false);
                    const staffPerf = staffPerfs.reduce((best: any, p: any) =>
                        !best || (p.duration ?? Infinity) < (best.duration ?? Infinity) ? p : best, null);
                    bestPerformanceId = staffPerf?.id;
                    if (staffPerf && isStaffAvailableForSlot(staff.id, staffPerf.id)) {
                        targetStaffId = staff.id;
                        bestPerformanceId = staffPerf.id;
                        foundFreeStaff = true;
                        break;
                    }
                }
            }

            // Se sono tutti occupati (o non hanno servizi), mostriamo errore e ci fermiamo
            if (!foundFreeStaff) {
                toast.error(t('calendar.all_staff_busy', "Nessun dipendente disponibile in questo orario."));
                return;
            }
        }

        if (!targetStaffId || !bestPerformanceId) {
            toast.error(t('calendar.no_service_for_staff', "Il dipendente selezionato non ha servizi assegnati."));
            return;
        }

        setNewAppointmentProps({
            initialDate: date,
            initialTime: time,
            initialStaffId: targetStaffId,
            initialPerformanceId: bestPerformanceId,
        });
        setIsNewAppointmentOpen(true);
    };

    const onSelectBookingResult = (booking: any) => {
        setCurrentDate(new Date(booking.date));
        setView('day');
        // Parse HH:mm into fractional hour for scroll centering
        if (booking.time) {
            const [h, m] = booking.time.split(':').map(Number);
            setScrollToHour({ value: h + m / 60, _t: Date.now() });
        }
        setSelectedBooking(booking);
        setIsBookingModalOpen(true);
    };

    // Navigate to day view when a grouped week-view event is clicked
    const handleGroupClick = (groupedEvent: any) => {
        // dayIndex is the position in weekDays array
        const targetDay = weekDays[groupedEvent.dayIndex];
        if (targetDay) setCurrentDate(targetDay);
        setView('day');
        setScrollToHour({ value: groupedEvent.startHour, _t: Date.now() });
    };

    const handleGoToToday = () => {
        const now = new Date();
        setCurrentDate(now);
        setScrollToHour({ value: now.getHours() + now.getMinutes() / 60, _t: Date.now() });
    };

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="h-[calc(100dvh-112px)] sm:h-[calc(100dvh-144px)] flex flex-col bg-white rounded-3xl shadow-sm overflow-hidden select-none"
            >
                <CalendarHeader
                    view={view}
                    setView={setView}
                    currentDateLabel={titleFormatter.format(currentDate)}
                    onPrev={() => navigateDate('prev')}
                    onNext={() => navigateDate('next')}
                    onToday={handleGoToToday}
                    onNewAppointment={() => {
                        setNewAppointmentProps({});
                        setIsNewAppointmentOpen(true);
                    }}
                    staffMembers={staffMembers}
                    selectedStaff={selectedStaff}
                    onSelectStaff={setSelectedStaff}
                    allStaffOption={isMobile ? undefined : ALL_STAFF}
                    currentDate={currentDate}
                    onSelectDate={setCurrentDate}
                    bookings={bookings}
                    clients={clients}
                    getPerformanceById={getPerformanceById}

                    onSelectBookingResult={onSelectBookingResult}
                />

                {/* Il riepilogo del giorno sta sotto la data, prima delle modalità */}
                {view === 'day' && (
                    <CalendarDayStats
                        date={currentDate}
                        staffMembers={staffMembers.map((s) => ({ ...s, time_ranges: (getUserById(s.id) as any)?.time_ranges })) as any}
                        bookings={bookings}
                        busySlots={busySlots}
                        shiftChanges={shiftChanges}
                        organization={organization}
                        getPerformanceById={getPerformanceById}
                        getBookingPrice={getBookingPrice}
                    />
                )}

                <CalendarModeToolbar
                    mode={agendaMode}
                    view={view}
                    movingLabel={movingBooking
                        ? `${getClientById(movingBooking.client_id)?.full_name || t('bookings.modal.client_not_found', 'Cliente')} (${(movingBooking.time || '').slice(0, 5)})`
                        : null}
                    onStartMove={() => { exitAgendaModes(); setIsMovePicking(true); }}
                    onToggleBlock={() => { const on = isBlockMode; exitAgendaModes(); setIsBlockMode(!on); }}
                    onToggleCancelled={() => { const on = showCancelled; exitAgendaModes(); setShowCancelled(!on); }}
                    onOpenCloseDay={() => { exitAgendaModes(); setIsCloseDayOpen(true); }}
                    onExit={exitAgendaModes}
                    onQuickSale={() => setIsQuickSaleOpen(true)}
                    onNotifyClients={isAdmin ? () => setIsNotifyClientsOpen(true) : undefined}
                />



                <CalendarGrid
                    view={view}
                    weekDays={weekDays}
                    hours={hours}
                    startHour={startHour}
                    endHour={endHour}
                    displayEvents={displayEvents}
                    currentDate={currentDate}
                    onEventClick={(b) => {
                        if (showCancelled) return; // sola lettura
                        // Primo step spostamento da toolbar: questo è l'appuntamento da spostare
                        if (isMovePicking) {
                            if (b.status !== 'confirmed') {
                                toast.error(t('calendar.toolbar.move_only_confirmed', 'Puoi spostare solo appuntamenti confermati.'));
                                return;
                            }
                            setIsMovePicking(false);
                            setMovingBooking(b);
                            return;
                        }
                        // In modalità spostamento il tap su un altro appuntamento cambia la selezione
                        if (movingBooking) { setMovingBooking(b); return; }
                        setSelectedBooking(b); setIsBookingModalOpen(true);
                    }}
                    onGroupClick={handleGroupClick}
                    onEmptySlotClick={handleEmptySlotClick}
                    onEventVerticalDrop={handleEventVerticalDrop}
                    onEventHorizontalDrop={handleEventHorizontalDrop}
                    onEventDrop2D={handleEventCombinedDrop}
                    t={t}
                    selectedStaff={selectedStaff}
                    staffMembers={staffMembers}
                    step={organization?.step || 15}
                    scrollToHour={scrollToHour}
                    moveTargets={moveTargets}
                    cancelledEvents={cancelledEvents}
                    readOnly={showCancelled}
                    canReorderStaff={isAdmin && !showCancelled}
                    onStaffReorder={handleStaffReorder}
                />
            </motion.div>

            {/* MODALE CHIUSURA GIORNATA */}
            <CloseDayModal
                isOpen={isCloseDayOpen}
                onClose={() => !isTogglingDay && setIsCloseDayOpen(false)}
                date={currentDate}
                staff={closeDayStaff}
                onConfirm={handleToggleStaffDay}
                isLoading={isTogglingDay}
            />

            {/* CONFERMA CHIUSURA GIORNATA CON APPUNTAMENTI */}
            <ConfirmDialog
                isOpen={!!confirmCloseDay}
                title={t('calendar.editday.confirm_title', 'Chiudi giornata')}
                description={t('calendar.editday.confirm_desc', '{{name}} ha {{count}} appuntamenti confermati in questa giornata. Chiudendola i clienti non potranno più prenotare, ma gli appuntamenti esistenti restano: spostali o avvisa i clienti.', { name: confirmCloseDay?.name, count: confirmCloseDay?.count })}
                confirmLabel={t('calendar.editday.confirm_btn', 'Chiudi comunque')}
                cancelLabel={t('common.cancel', 'Annulla')}
                variant="danger"
                loading={isTogglingDay}
                onConfirm={() => confirmCloseDay && doCloseStaffDay(confirmCloseDay.staffId)}
                onCancel={() => !isTogglingDay && setConfirmCloseDay(null)}
            />

            {/* MODALE NUOVO APPUNTAMENTO */}
            <NewAppointmentModal
                isOpen={isNewAppointmentOpen}
                onClose={() => setIsNewAppointmentOpen(false)}
                {...newAppointmentProps}
            />

            {/* MODALE DETTAGLI PRENOTAZIONE */}
            {isBookingModalOpen && selectedBooking && (
                <BookingModal
                    isOpen={isBookingModalOpen}
                    booking={selectedBooking}
                    hairdresser={getUserById(selectedBooking.hairdresser_id)}
                    client={getClientById(selectedBooking.client_id)}
                    performance={getPerformanceById(selectedBooking.performance_id)}
                    onClose={() => {
                        setIsBookingModalOpen(false);
                        setSelectedBooking(null);
                    }}
                    onOpenCustomer={(client) => {
                        setSelectedCustomerForDetail(client);
                        setIsCustomerDetailOpen(true);
                    }}
                />
            )}

            {/* MODALE DETTAGLI CLIENTE */}
            {isCustomerDetailOpen && selectedCustomerForDetail && (
                <CustomerDetailModal
                    customer={mapRawClientToCustomer(
                        selectedCustomerForDetail,
                        bookings,
                        t('customers.never_visited')
                    )}
                    onClose={() => {
                        setIsCustomerDetailOpen(false);
                        setSelectedCustomerForDetail(null);
                    }}
                />
            )}

            {/* VENDITA AL BANCO */}
            <QuickSaleModal
                isOpen={isQuickSaleOpen}
                onClose={() => setIsQuickSaleOpen(false)}
            />

            {/* AVVISA I CLIENTI — precompilato sul giorno visualizzato.
                In vista Settimana non c'è un giorno solo da precompilare: si apre su "Tutti". */}
            <SendNotificationModal
                isOpen={isNotifyClientsOpen}
                onClose={() => setIsNotifyClientsOpen(false)}
                preset={notifyPreset}
            />

            {/* MODALE CONFERMA SPOSTAMENTO */}
            <ConfirmDialog
                isOpen={confirmDrop.isOpen}
                title={t('calendar.confirm_move_title', 'Spostamento appuntamento')}
                description={t('calendar.confirm_move_desc', 'Vuoi confermare lo spostamento di questo appuntamento?')}
                confirmLabel={t('common.confirm', 'Conferma')}
                cancelLabel={t('common.cancel', 'Annulla')}
                onConfirm={confirmDrop.onConfirm}
                onCancel={confirmDrop.onCancel}
            />
        </>
    );
};

export default Calendar;